import type { SatDomain } from '@/api/sat';

/**
 * The homepage difficulty ladder (spec 010 HOME-V3 §B, fixture table): four real
 * bank items, one area (Algebra), one skill thread (Linear Functions).
 *
 * Each is keyed on the bank's stable `source` code, never a UUID: seeded question ids
 * are `gen_random_uuid()` per environment, so an id fixture would 404 in prod.
 * `POST /sat/open/check` resolves `sourceCode` server-side.
 *
 * TEXT ONLY: never the answer key or the explanation (both come back from `check`).
 *
 * VERIFIED 2026-10-06 against the local bank: all four are `QB-` practice-pool rows
 * (`is_active`, NOT `is_diagnostic_only`), MATH / ALGEBRA / Linear Functions. The Prove-it
 * item is HARD in the bank (there are three difficulties); "Prove it" is the film's own
 * word for the mastery moment, not a fourth difficulty. If an item is ever retired,
 * `check` answers 404 and the ladder silently swaps in a live question.
 */
export type LadderKey = 'EASY' | 'MEDIUM' | 'HARD' | 'PROVE_IT';

export interface LadderQuestion {
  /** Stable key for React + persistence: the source code for fixtures, the UUID for live questions. */
  key: string;
  /** Present for fixtures; `check` is called with `sourceCode`. */
  sourceCode?: string;
  /** Present for live questions; `check` is called with `questionId`. */
  questionId?: string;
  domain: SatDomain;
  skill: string | null;
  stem: string;
  choices: { key: 'A' | 'B' | 'C' | 'D'; text: string }[];
}

export interface LadderLevel {
  key: LadderKey;
  label: string;
  /** The bank difficulty a live replacement should match. */
  difficulty: 'EASY' | 'MEDIUM' | 'HARD';
  /** After "{skill} · ": what the question tests. */
  tests: string;
  question: LadderQuestion;
}

export type LadderSubject = 'MATH' | 'RW';

export interface Ladder {
  subject: LadderSubject;
  /** Toggle label. */
  label: string;
  /** The one area this ladder fills (its ring on the page). */
  domain: SatDomain;
  skill: string;
  levels: readonly LadderLevel[];
}

export const LADDER_SKILL = 'Linear Functions';
export const LADDER_DOMAIN: SatDomain = 'ALGEBRA';

const qOf =
  (domain: SatDomain, skill: string) =>
  (sourceCode: string, stem: string, texts: [string, string, string, string]): LadderQuestion => ({
    key: sourceCode,
    sourceCode,
    domain,
    skill,
    stem,
    choices: texts.map((text, i) => ({ key: (['A', 'B', 'C', 'D'] as const)[i], text })),
  });
const q = qOf(LADDER_DOMAIN, LADDER_SKILL);

export const MATH_LEVELS: readonly LadderLevel[] = [
  {
    key: 'EASY',
    label: 'Easy',
    difficulty: 'EASY',
    tests: 'Read a function and evaluate it.',
    question: q('QB-AL-079', 'If f(x) = 4x − 3, what is f(5)?', ['7', '12', '17', '23']),
  },
  {
    key: 'MEDIUM',
    label: 'Medium',
    difficulty: 'MEDIUM',
    tests: 'Find the rate from two values, then extend it.',
    question: q('QB-AL-092', 'A linear function f has f(2) = 9 and f(5) = 18. What is f(8)?', ['24', '27', '30', '33']),
  },
  {
    key: 'HARD',
    label: 'Hard',
    difficulty: 'HARD',
    tests: 'Slope with no points given.',
    question: q('QB-AL-098', 'The linear function h satisfies h(2) − h(−1) = 12. What is the slope of h?', ['2', '3', '4', '6']),
  },
  {
    key: 'PROVE_IT',
    label: 'Prove it',
    difficulty: 'HARD',
    tests: 'Spot when two lines can never meet.',
    question: q(
      'QB-AL-100',
      'Lines j and k are defined by the equations y = 2x − 5 and y = 2x + c, respectively. For which value of c do lines j and k have no intersection?',
      ['c = 5', 'c = −5', 'c = 0', 'Any c ≠ −5'],
    ),
  },
] as const;

/** Back-compat name for the Math ladder. */
export const LADDER_LEVELS = MATH_LEVELS;

/**
 * The Reading & Writing ladder: Precision & Clarity (Expression of Ideas). Chosen over the
 * Standard English Conventions items because 184 of 188 of those carry key A, which makes a
 * ladder of them a tap-A game; these keys are spread. VERIFIED 2026-10-06 against the local bank:
 * all four are `QB-` practice-pool rows (`is_active`, NOT `is_diagnostic_only`), EXPRESSION_OF_IDEAS
 * / Precision & Clarity. Still QB-* items (originality issue #324): swap when non-QB R&W exists.
 */
export const RW_SKILL = 'Precision & Clarity';
export const RW_DOMAIN: SatDomain = 'EXPRESSION_OF_IDEAS';
const r = qOf(RW_DOMAIN, RW_SKILL);

export const RW_LEVELS: readonly LadderLevel[] = [
  {
    key: 'EASY',
    label: 'Easy',
    difficulty: 'EASY',
    tests: 'Pick the formal, precise phrase.',
    question: r(
      'QB-EI-103',
      "The researcher's analysis revealed that the intervention ______ patient outcomes.\n\nWhich choice maintains the most appropriately formal and precise tone?",
      ['really helped with', 'did a lot for', 'significantly improved', 'made things way better for'],
    ),
  },
  {
    key: 'MEDIUM',
    label: 'Medium',
    difficulty: 'MEDIUM',
    tests: 'Choose the exact term for the job.',
    question: r(
      'QB-EI-118',
      "The study's sample size was too small to ______ the results to the general population.\n\nWhich choice most precisely and formally conveys the intended meaning?",
      ['apply', 'generalize', 'use for', 'expand'],
    ),
  },
  {
    key: 'HARD',
    label: 'Hard',
    difficulty: 'HARD',
    tests: 'Say what the study shows, no more and no less.',
    question: r(
      'QB-EI-121',
      'The new training program ______ employee productivity.\n\nWhich choice most clearly indicates that the effect observed in the study was statistically meaningful but modest in practical terms?',
      ['dramatically increased', 'failed to improve', 'produced a statistically significant but small improvement in', 'had a positive effect on'],
    ),
  },
  {
    key: 'PROVE_IT',
    label: 'Prove it',
    difficulty: 'HARD',
    tests: 'Cut every redundant word and keep the meaning.',
    question: r(
      'QB-EI-125',
      'The merger, if completed, will create the largest financial services company in the country by total assets under management.\n\nWhich choice is the most precise and avoids both redundancy and unintended ambiguity?',
      [
        'The merger, if it goes through and gets done, will result in the creation of the biggest and largest financial services company in the country based on total assets under management.',
        "If completed, the merger will create the country's largest financial services company by total assets under management.",
        'Should the merger be completed and finalized, it would create what would be the largest financial services company, measured by total assets managed, in the country.',
        'The merger, upon its completion, would create the financial services company with the largest amount of total assets under management in the whole country.',
      ],
    ),
  },
] as const;

export const LADDERS: Record<LadderSubject, Ladder> = {
  MATH: { subject: 'MATH', label: 'Math', domain: LADDER_DOMAIN, skill: LADDER_SKILL, levels: MATH_LEVELS },
  RW: { subject: 'RW', label: 'Reading & Writing', domain: RW_DOMAIN, skill: RW_SKILL, levels: RW_LEVELS },
};

export const PROVE_IT_EXPLAINER =
  'In the course, a mastery check is a short set across the whole area. Pass it and the area turns green. This is the kind of question it asks.';

/** Outcome copy per case (HOME-V3 copy table). */
export function outcomeLine(level: LadderKey, correct: boolean, area = 'Algebra'): string {
  if (level === 'PROVE_IT') {
    return correct
      ? `Proven. In the course, ${area} turns green.`
      : "Not yet. The course only asks you to prove an area once practice says you're ready.";
  }
  return correct
    ? 'Right. In the course, the next set would push harder.'
    : 'Not yet. In the course, practice would aim right here until it moves.';
}

/**
 * What each area covers, in the bank's own skill names (VERIFIED 2026-10-06 against
 * `sat_skills`). Shown when a group expands. Plain words, no scoring talk.
 */
export const DOMAIN_BLURBS: Readonly<Record<SatDomain, string>> = {
  INFORMATION_AND_IDEAS: 'Main ideas, inference, details, data.',
  CRAFT_AND_STRUCTURE: 'Words in context, tone, structure.',
  EXPRESSION_OF_IDEAS: 'Transitions, precision, synthesis.',
  STANDARD_ENGLISH_CONVENTIONS: 'Commas, pronouns, agreement, boundaries.',
  ALGEBRA: 'Linear equations, systems, inequalities.',
  ADVANCED_MATH: 'Quadratics, exponentials, polynomials.',
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: 'Ratios, percents, statistics, tables.',
  GEOMETRY_AND_TRIGONOMETRY: 'Triangles, circles, volume, trig.',
};

/**
 * The method, as strongly as the product behaves (checked against the backend 2026-10-06):
 * practice sets aim at the weakest area and repeat until it moves, then the course rotates to
 * the next weakest. There is NO spaced-repetition or review scheduling (staleness only raises an
 * informational signal for tutors), so this line never says "spaced".
 */
export const METHOD_LINE =
  'Practice goes where you need it most and repeats until that area moves, so every hour works toward a higher score.';
